import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Plus, Pencil, Trash2, Printer, ArrowRightLeft, Download } from "lucide-react";
import { useAuth } from "@/AuthContext";
import { API } from "@/config";

export const StatementView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [payments, setPayments] = useState([]);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    fetchInvoice();
    fetchPayments();
    fetchLogo();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}`);
      setInvoice(response.data);
    } catch (error) {
      toast.error("Error fetching invoice");
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments/invoice/${id}`);
      setPayments(response.data);
    } catch (error) {
      console.log("No payments yet");
    }
  };

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/logo`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      setLogoUrl(url);
    } catch (error) {
      console.log("No logo uploaded yet");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const downloadStatementPDF = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}/statement-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Statement_${invoice.invoice_number}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Statement PDF downloaded - attach it to your email");
    } catch (error) {
      toast.error("Error generating PDF");
    }
  };

  const handleSendEmail = () => {
    // First, trigger PDF download so user can attach it
    downloadStatementPDF();
    
    // Then open Gmail compose
    const customerName = invoice.customer_name.split(' ')[0]; // First name
    const fullName = invoice.customer_name;
    const dueAmount = paymentDueThisMonth.toFixed(2);
    const dueDate = nextDueDate || 'the 15th of this month';
    const monthlyPayment = invoice.payment_plan.enabled 
      ? invoice.payment_plan.monthly_payment.toFixed(2)
      : (invoice.lease_plan && invoice.lease_plan.enabled ? invoice.lease_plan.monthly_payment.toFixed(2) : '0.00');
    const remainingBalance = balanceDue.toFixed(2);
    
    const subject = `Payment Reminder - Invoice ${invoice.invoice_number}`;
    
    // Create statement copy focused on monthly payment
    const statementCopy = `
═══════════════════════════════════════════════════════════════
                         PAYMENT STATEMENT
                         Ellantes LLC
                    1309 Coffeen Avenue
                  Sheridan, WY 82801
                    EIN: 33-3145594
═══════════════════════════════════════════════════════════════

Invoice Number: ${invoice.invoice_number}
Statement Date: ${statementDate}

Account Holder: ${fullName}
${invoice.customer_address}
${invoice.customer_email}

───────────────────────────────────────────────────────────────
                      PAYMENT DUE THIS MONTH
───────────────────────────────────────────────────────────────

Amount Due:                                          $${dueAmount}
Due Date:                                     ${dueDate}

${remainingPayments > 0 ? `Remaining Payments:                     ${remainingPayments} payment${remainingPayments !== 1 ? 's' : ''} left` : ''}
Remaining Balance:                                   $${remainingBalance}

═══════════════════════════════════════════════════════════════`;

    let paymentMethods = `
PAYMENT METHODS:

• Zelle: Ellantesmarketing@gmail.com
• Bank Transfer (ACH):
  - Routing Number: 021000021
  - Account Number: 2900199576`;

    // Add payment link if available
    if (invoice.payment_link && invoice.payment_link.trim()) {
      paymentMethods = `
PAYMENT METHODS:

🔗 Pay Online: ${invoice.payment_link}

Or use one of these methods:
• Zelle: Ellantesmarketing@gmail.com
• Bank Transfer (ACH):
  - Routing Number: 021000021
  - Account Number: 2900199576`;
    }
    
    const body = `Dear ${customerName},

I hope this message finds you well!

This is a friendly reminder that your monthly payment for Invoice ${invoice.invoice_number} is coming up soon. Please find your payment statement below:
${statementCopy}
${paymentMethods}

If you've already made this payment, please disregard this reminder, and thank you!

If you have any questions or need to discuss your payment plan, please don't hesitate to reach out.

Thank you for your continued business!

Best regards,
Ellantes LLC
1309 Coffeen Avenue
Sheridan, WY 82801
EIN: 33-3145594`;

    // Open Gmail compose with pre-filled content
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(invoice.customer_email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank');
  };

  if (!invoice) {
    return <div>Loading...</div>;
  }

  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const balanceDue = invoice.total - totalPaid;
  const currentDate = new Date();
  const statementDate = currentDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  
  // Calculate remaining payments and payment due this month
  let paymentDueThisMonth = 0;
  let remainingPayments = 0;
  let nextDueDate = '';
  
  if (invoice.payment_plan.enabled && balanceDue > 0) {
    // Calculate remaining payments
    const monthlyPayment = invoice.payment_plan.monthly_payment;
    remainingPayments = Math.ceil(balanceDue / monthlyPayment);
    
    // Payment due this month
    paymentDueThisMonth = Math.min(monthlyPayment, balanceDue);
    
    // Next due date is the 15th of current or next month
    const today = new Date();
    let dueDate = new Date(today.getFullYear(), today.getMonth(), 15);
    if (today.getDate() > 15) {
      // If today is after the 15th, next payment is the 15th of next month
      dueDate = new Date(today.getFullYear(), today.getMonth() + 1, 15);
    }
    nextDueDate = dueDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } else if (invoice.lease_plan && invoice.lease_plan.enabled && balanceDue > 0) {
    // Calculate remaining payments for lease-to-own
    const monthlyPayment = invoice.lease_plan.monthly_payment;
    remainingPayments = Math.ceil(balanceDue / monthlyPayment);
    
    // Payment due this month
    paymentDueThisMonth = Math.min(monthlyPayment, balanceDue);
    
    // Next due date is the 15th of current or next month
    const today = new Date();
    let dueDate = new Date(today.getFullYear(), today.getMonth(), 15);
    if (today.getDate() > 15) {
      dueDate = new Date(today.getFullYear(), today.getMonth() + 1, 15);
    }
    nextDueDate = dueDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } else if (balanceDue > 0) {
    // If no payment plan, full balance is due
    paymentDueThisMonth = balanceDue;
    remainingPayments = 1;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between print:hidden">
        <h1 className="text-4xl font-bold" data-testid="statement-view-title">Monthly Statement</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/invoices/${id}`)} data-testid="back-to-invoice-btn">
            Back to Invoice
          </Button>
          <Button variant="outline" onClick={downloadStatementPDF} data-testid="download-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
          <Button onClick={handleSendEmail} className="bg-blue-600 hover:bg-blue-700" data-testid="send-email-btn">
            <FileText className="h-4 w-4 mr-2" />
            Send Payment Reminder
          </Button>
          <Button onClick={handlePrint} data-testid="print-statement-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print Statement
          </Button>
        </div>
      </div>

      <Card className="print:shadow-none" data-testid="statement-card">
        <CardContent className="p-8">
          <div className="space-y-8 invoice-content">
            {/* Header */}
            <div className="flex justify-between items-start border-b-2 pb-6">
              <div>
                {logoUrl && (
                  <img src={logoUrl} alt="Ellantes Logo" className="h-20 mb-4" data-testid="statement-logo" />
                )}
                <div className="space-y-1">
                  <div className="text-xl font-bold">Ellantes LLC</div>
                  <div className="text-sm text-gray-600">1309 Coffeen Avenue</div>
                  <div className="text-sm text-gray-600">Sheridan, WY 82801</div>
                  <div className="text-sm text-gray-600">United States</div>
                  <div className="text-sm text-gray-600 mt-2">EIN: 33-3145594</div>
                </div>
              </div>
              <div className="text-right">
                <h2 className="text-3xl font-bold mb-4">PAYMENT STATEMENT</h2>
                <div className="text-lg font-semibold text-blue-600">{invoice.invoice_number}</div>
                <div className="text-sm text-gray-600 mt-2">Statement Date: {statementDate}</div>
              </div>
            </div>

            {/* Customer Info */}
            <div>
              <h3 className="font-bold text-lg mb-2">Account Information:</h3>
              <div>
                <div className="font-semibold">{invoice.customer_name}</div>
                <div>{invoice.customer_address}</div>
                <div>{invoice.customer_email}</div>
                <div>{invoice.customer_phone}</div>
              </div>
            </div>

            {/* Summary Box */}
            <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200 print-keep-together">
              <h3 className="font-bold text-xl mb-4">Account Summary</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-base">
                  <div>
                    <div className="text-gray-600">Original Invoice Amount:</div>
                    <div className="font-bold">${invoice.total.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Total Payments Received:</div>
                    <div className="font-bold text-green-600">${totalPaid.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Remaining Balance:</div>
                    <div className="font-bold">${balanceDue.toFixed(2)}</div>
                  </div>
                  {(invoice.payment_plan.enabled || (invoice.lease_plan && invoice.lease_plan.enabled)) && balanceDue > 0 && (
                    <div>
                      <div className="text-gray-600">Monthly Payment:</div>
                      <div className="font-bold">${(invoice.payment_plan.enabled ? invoice.payment_plan.monthly_payment : invoice.lease_plan.monthly_payment).toFixed(2)}</div>
                    </div>
                  )}
                  {remainingPayments > 0 && (
                    <div>
                      <div className="text-gray-600">Remaining Payments:</div>
                      <div className="font-bold text-blue-600">{remainingPayments} payment{remainingPayments !== 1 ? 's' : ''} left</div>
                    </div>
                  )}
                  {nextDueDate && (
                    <div>
                      <div className="text-gray-600">Next Payment Due:</div>
                      <div className="font-bold text-orange-600">{nextDueDate}</div>
                    </div>
                  )}
                </div>
                
                <div className="border-t-2 border-blue-300 pt-4">
                  <div className="bg-red-100 border-2 border-red-300 rounded-lg p-4">
                    <div className="text-gray-700 font-semibold mb-1">PAYMENT DUE THIS MONTH:</div>
                    <div className={`text-4xl font-bold ${paymentDueThisMonth > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      ${paymentDueThisMonth.toFixed(2)}
                    </div>
                    {nextDueDate && (
                      <div className="text-sm text-red-700 font-semibold mt-2">
                        Due Date: {nextDueDate}
                      </div>
                    )}
                    {(invoice.payment_plan.enabled || (invoice.lease_plan && invoice.lease_plan.enabled)) && balanceDue > 0 && (
                      <div className="text-sm text-gray-600 mt-2">
                        {paymentDueThisMonth < balanceDue ? 
                          `(Regular monthly payment)` :
                          '(Final payment)'
                        }
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="print-keep-together">
              <h3 className="font-bold text-lg mb-3">Original Invoice Items</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="font-medium">{item.product_name}</div>
                        {item.description && (
                          <div className="text-sm text-gray-600">{item.description}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right">${item.price.toFixed(2)}</TableCell>
                      <TableCell className="text-right">${item.total.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <div className="flex justify-end mt-4">
                <div className="w-64 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>${invoice.subtotal.toFixed(2)}</span>
                  </div>
                  {invoice.discount_amount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount:</span>
                      <span>-${invoice.discount_amount.toFixed(2)}</span>
                    </div>
                  )}
                  {invoice.tax_rate > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Tax ({invoice.tax_rate}%):</span>
                      <span>${invoice.tax_amount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold border-t pt-1">
                    <span>Invoice Total:</span>
                    <span>${invoice.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment History */}
            <div className="print-keep-together">
              <h3 className="font-bold text-lg mb-3">Payment History</h3>
              {payments.length > 0 ? (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Payment Method</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>{new Date(payment.payment_date).toLocaleDateString()}</TableCell>
                          <TableCell className="capitalize">{payment.payment_method.replace('_', ' ')}</TableCell>
                          <TableCell>{payment.reference_number || '-'}</TableCell>
                          <TableCell className="text-right font-semibold">${payment.amount.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-gray-100 font-bold">
                        <TableCell colSpan={3} className="text-right">Total Paid:</TableCell>
                        <TableCell className="text-right text-green-600">${totalPaid.toFixed(2)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </>
              ) : (
                <div className="text-center py-6 bg-gray-50 rounded-lg">
                  <p className="text-gray-600">No payments received yet</p>
                </div>
              )}
            </div>

            {/* Payment Plan Info */}
            {invoice.payment_plan.enabled && (
              <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200 print-keep-together">
                <h3 className="font-bold text-lg mb-3">Payment Plan Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Monthly Payment:</div>
                    <div className="font-bold text-lg">${invoice.payment_plan.monthly_payment.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Number of Payments:</div>
                    <div className="font-bold text-lg">{invoice.payment_plan.number_of_months} months</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">APR:</div>
                    <div className="font-bold">{invoice.payment_plan.apr}%</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Down Payment:</div>
                    <div className="font-bold">${invoice.payment_plan.down_payment.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Instructions */}
            <div className="bg-gray-50 p-6 rounded-lg print-keep-together">
              <h3 className="font-bold text-lg mb-3">Payment Instructions</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2">Zelle Payment</h4>
                  <p className="text-sm">Email: <span className="font-mono">Ellantesmarketing@gmail.com</span></p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Bank Transfer (ACH)</h4>
                  <p className="text-sm">Routing: <span className="font-mono">021000021</span></p>
                  <p className="text-sm">Account: <span className="font-mono">2900199576</span></p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-300">
                <p className="text-sm text-gray-700">
                  <strong>Payment Terms:</strong> {invoice.payment_terms}
                </p>
                <p className="text-sm text-red-600 mt-2">
                  <strong>Note:</strong> Late payments may incur additional fees. Please remit payment promptly.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center text-sm text-gray-600 border-t pt-4">
              <p>For questions about this statement, please contact us at Ellantesmarketing@gmail.com</p>
              <p className="mt-2">Thank you for your business!</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

